import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';

// 進行中ジョブの型（async_generation_jobsテーブルから）
export interface InProgressJob {
  id: string;
  service_type: string;
  job_type?: string;
  status: string;
  progress?: number;
  created_at: string;
  completed_at?: string;
  error_message?: string;
}

export type GenerationJobKind = 'image' | 'video';

// ユーザー起源の入力のみ格納する安全なデータ構造。
// 内部で組み立てたプロンプトや商品JSON・レギュレーション等は絶対に含めない。
export interface UserInputs {
  mode: 'prompt' | 'attributes' | 'scenario' | 'unknown';
  prompt?: string;
  negative_prompt?: string;
  attributes?: Record<string, unknown>;
  additional_instruction?: string;
  /** 動画: 動画ボックスの三点メニューで入力した追加指示プロンプト */
  additional_prompt?: string;
  /** 動画: シナリオ生成時に入力した追加プロンプト (受付時に DB から解決) */
  scenario_additional_prompt?: string;
  scene_id?: string;
  scenario_id?: string;
}

export interface GenerationJob {
  id: string;
  material_id?: string;
  status: string;
  created_at: string;
  model: string;
  asset_count: number;
  result?: Record<string, unknown>;
  image_url?: string;
  thumbnail_url?: string;
  storage_bucket?: string;
  storage_path?: string;
  is_deleted?: boolean;
  error_message?: string;
  job_type?: string;
  media_type?: 'image' | 'video';
  user_inputs?: UserInputs;
  // 動画ジョブ固有フィールド
  video_model?: string;
  resolution?: string | number;
  aspect_ratio?: string;
  duration_seconds?: number;
  generate_audio?: boolean;
  resize_mode?: string;
  scene_id?: string | null;
  scenario_id?: string | null;
}

interface JobsResponse {
  items: GenerationJob[];
  has_more: boolean;
}

async function fetchJobs(limit: number = 20, offset: number = 0, kind?: GenerationJobKind): Promise<JobsResponse> {
  const params: Record<string, string | number> = { limit, offset };
  if (kind) params.kind = kind;
  const { data } = await apiClient.get<JobsResponse>('/generation/jobs', { params });
  return data;
}

/**
 * 画像生成ジョブ履歴を取得するフック（最新5件のみ、通知用）
 */
export function useGenerationJobs(kind?: GenerationJobKind) {
  return useQuery<GenerationJob[]>({
    queryKey: ['generation', 'jobs', 'recent', kind ?? 'all'],
    queryFn: async () => {
      const response = await fetchJobs(5, 0, kind);
      return response.items;
    },
    staleTime: 30_000,
  });
}

/**
 * 画像生成ジョブ履歴を無限スクロールで取得するフック
 */
export function useInfiniteGenerationJobs(pageSize: number = 20, kind?: GenerationJobKind) {
  return useInfiniteQuery({
    queryKey: ['generation', 'jobs', 'infinite', kind ?? 'all'],
    queryFn: async ({ pageParam = 0 }) => {
      return fetchJobs(pageSize, pageParam, kind);
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length * pageSize;
    },
    initialPageParam: 0,
    staleTime: 30_000,
  });
}

/**
 * 進行中のジョブを取得するフック（通知用）
 */
export function useInProgressJobs() {
  return useQuery<InProgressJob[]>({
    queryKey: ['jobs', 'in-progress'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ jobs: InProgressJob[] }>('/jobs', {
        params: { statuses: 'pending,processing', limit: 10 }
      });
      return data.jobs;
    },
    staleTime: 10_000, // 10秒キャッシュ
  });
}

/**
 * ジョブをキャンセルするフック
 */
export function useCancelJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      await apiClient.delete(`/jobs/${jobId}`);
    },
    onSuccess: () => {
      // 進行中ジョブリストを再取得
      queryClient.invalidateQueries({ queryKey: ['jobs', 'in-progress'] });
    },
  });
}
